import { useEffect, useMemo, useState } from 'react';
import { useAlertSettings, useSaveAlertSettings } from '@/hooks/useAlertSettings';
import { useFunds } from '@/hooks/useFunds';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useToast } from '@/components/ToastProvider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Toggle } from '@/components/Toggle';
import { PageSpinner } from '@/components/PageSpinner';
import { ErrorState } from '@/components/ErrorState';
import { ApiError } from '@/lib/errors';
import type { AlertSettings, FundAlertSettings, FundModel } from '@/types/model.types';

const MAX_THRESHOLD = 9999999999;

/**
 * Port of alerts.page.ts / alerts.page.html.
 *
 * The Angular version tracked dirty state by copying every field into a
 * parallel original* field on the same object and comparing the two. Here the
 * saved settings are the query cache and the draft is component state, so
 * "has unsaved changes" is one comparison and Cancel is one assignment.
 */
export default function AlertsPage() {
  const { showToast } = useToast();
  const { data: profile } = useUserProfile();
  const fundsQuery = useFunds();
  const settingsQuery = useAlertSettings();
  const saveSettings = useSaveAlertSettings();

  const [draft, setDraft] = useState<AlertSettings | null>(null);

  // Saved settings merged against the full fund list, so funds with no
  // configured alert still get a row. Same merge as Angular's loadFundAlerts.
  const saved = useMemo(() => {
    if (!fundsQuery.data || !settingsQuery.data) return null;
    return mergeFundsIntoSettings(fundsQuery.data, settingsQuery.data);
  }, [fundsQuery.data, settingsQuery.data]);

  useEffect(() => {
    if (saved) setDraft(saved);
  }, [saved]);

  if (fundsQuery.isError || settingsQuery.isError) {
    return (
      <ErrorState
        title="Could not load alert settings"
        error={fundsQuery.error ?? settingsQuery.error}
        onRetry={() => {
          void fundsQuery.refetch();
          void settingsQuery.refetch();
        }}
      />
    );
  }

  if (fundsQuery.isPending || settingsQuery.isPending || !draft || !saved) {
    return <PageSpinner message="Loading alert settings..." />;
  }

  const hasUnsavedChanges = JSON.stringify(draft) !== JSON.stringify(saved);
  const activeCount = draft.fundAlertSettings.reduce(
    (count, fund) => count + (fund.largestActive ? 1 : 0) + (fund.cumulativeActive ? 1 : 0),
    0,
  );
  const totalCount = draft.fundAlertSettings.length * 2;

  const updateFund = (index: number, changes: Partial<FundAlertSettings>) => {
    setDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        fundAlertSettings: current.fundAlertSettings.map((fund, i) =>
          i === index ? { ...fund, ...changes } : fund,
        ),
      };
    });
  };

  const handleSave = () => {
    saveSettings.mutate(draft, {
      onSuccess: () => showToast('Alert settings saved successfully'),
      onError: (error) =>
        showToast(
          `Failed to save alert settings: ${
            error instanceof ApiError ? error.userMessage : 'Please try again.'
          }`,
          'danger',
        ),
    });
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6">
      <header className="text-center">
        <h1 className="text-2xl font-semibold text-slate-900">Fund alerts</h1>
        <p className="mt-1 text-gray1">Configure notifications for net sales thresholds</p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Notification settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-900">Alert via email</p>
              <p className="text-sm text-gray1">{profile?.email ?? profile?.loginId ?? ''}</p>
            </div>
            <Toggle
              label="Alert via email"
              checked={draft.alertViaEmail}
              onChange={(alertViaEmail) => setDraft({ ...draft, alertViaEmail })}
            />
          </div>

          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-900">Alert via SMS</p>
              <p className="text-sm text-gray1">{profile?.phoneNumber ?? ''}</p>
            </div>
            <Toggle
              label="Alert via SMS"
              checked={draft.alertViaSms}
              onChange={(alertViaSms) => setDraft({ ...draft, alertViaSms })}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Fund alert settings</CardTitle>
        </CardHeader>
        <CardContent className="px-2 sm:px-5">
          {/*
            The Angular template duplicated this whole table, once for desktop
            and once for phones. One responsive grid covers both: on small
            screens the toggle and threshold stack inside each cell.
          */}
          <div className="hidden grid-cols-12 gap-2 border-b border-slate-200 pb-2 text-center text-sm font-semibold text-slate-700 md:grid">
            <div className="col-span-2 text-left">Ticker</div>
            <div className="col-span-5">Largest trade alert</div>
            <div className="col-span-5">Net sales alert</div>
          </div>

          <ul className="divide-y divide-slate-100">
            {draft.fundAlertSettings.map((fund, index) => (
              <li key={fund.fundId} className="grid grid-cols-12 items-center gap-2 py-3">
                <div className="col-span-12 font-semibold text-slate-900 md:col-span-2">
                  {fund.fundTicker}
                </div>

                <ThresholdCell
                  legend="Largest trade"
                  ticker={fund.fundTicker}
                  active={fund.largestActive}
                  threshold={fund.largestThreshold}
                  onActiveChange={(largestActive) => updateFund(index, { largestActive })}
                  onThresholdChange={(largestThreshold) => updateFund(index, { largestThreshold })}
                />

                <ThresholdCell
                  legend="Net sales"
                  ticker={fund.fundTicker}
                  active={fund.cumulativeActive}
                  threshold={fund.cumulativeThreshold}
                  onActiveChange={(cumulativeActive) => updateFund(index, { cumulativeActive })}
                  onThresholdChange={(cumulativeThreshold) =>
                    updateFund(index, { cumulativeThreshold })
                  }
                />
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-slate-700">
            {activeCount} of {totalCount} fund alerts active
          </p>
          <div className="flex gap-3">
            <Button
              variant="outline"
              disabled={!hasUnsavedChanges || saveSettings.isPending}
              onClick={() => setDraft(saved)}
            >
              Cancel
            </Button>
            <Button disabled={!hasUnsavedChanges || saveSettings.isPending} onClick={handleSave}>
              {saveSettings.isPending ? 'Saving...' : 'Save all settings'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface ThresholdCellProps {
  legend: string;
  ticker: string;
  active: boolean;
  threshold: number;
  onActiveChange: (active: boolean) => void;
  onThresholdChange: (threshold: number) => void;
}

/**
 * Replaces the keypress/paste/input/blur quartet the Angular page used to keep
 * this field numeric. inputMode="numeric" gives phones the number pad, and one
 * sanitising onChange covers typing, pasting and drag-and-drop alike.
 */
function ThresholdCell({
  legend,
  ticker,
  active,
  threshold,
  onActiveChange,
  onThresholdChange,
}: ThresholdCellProps) {
  return (
    <div className="col-span-12 flex items-center gap-3 md:col-span-5 md:justify-center">
      <span className="w-24 text-sm text-gray1 md:hidden">{legend}</span>
      <Toggle label={`${legend} alert for ${ticker}`} checked={active} onChange={onActiveChange} />
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder="0"
        aria-label={`${legend} threshold for ${ticker}`}
        disabled={!active}
        value={threshold === 0 ? '' : String(threshold)}
        onChange={(event) => {
          const digitsOnly = event.target.value.replace(/[^0-9]/g, '');
          const parsed = digitsOnly === '' ? 0 : Number(digitsOnly);
          onThresholdChange(Math.min(parsed, MAX_THRESHOLD));
        }}
        className="w-32 rounded-md border border-slate-300 px-3 py-1.5 text-right tabular-nums focus-visible:border-primary focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary disabled:bg-slate-100 disabled:text-slate-400"
      />
    </div>
  );
}

/**
 * Every fund gets a row, whether or not the backend has alert settings for it.
 * Same behaviour as the Angular tickerToAlert merge.
 */
function mergeFundsIntoSettings(funds: FundModel[], settings: AlertSettings): AlertSettings {
  const byTicker = new Map(settings.fundAlertSettings.map((alert) => [alert.fundTicker, alert]));

  return {
    alertViaEmail: settings.alertViaEmail ?? false,
    alertViaSms: settings.alertViaSms ?? false,
    fundAlertSettings: funds.map((fund) => {
      const existing = byTicker.get(fund.ticker);
      return {
        fundId: existing?.fundId ?? fund.fundId,
        fundTicker: fund.ticker,
        largestActive: existing?.largestActive ?? false,
        largestThreshold: existing?.largestThreshold ?? 0,
        cumulativeActive: existing?.cumulativeActive ?? false,
        cumulativeThreshold: existing?.cumulativeThreshold ?? 0,
      };
    }),
  };
}
